import { sanitizePlayerName } from './format';

export const CHAT_MESSAGE_MAX_LENGTH = 280;
export const CHAT_USERNAME_MAX_LENGTH = 20;
export const CHAT_SEND_COOLDOWN_MS = 1_200;

export interface ChatMessageView {
  readonly id: string;
  readonly uid: string;
  readonly name: string;
  readonly text: string;
  readonly createdAt: number;
}

export interface ChatBatchView {
  readonly messages: readonly ChatMessageView[];
  readonly hasMore: boolean;
}

export interface ChatUiPort {
  readonly currentUserId: string | null;
  connect(onMessages: (batch: ChatBatchView) => void, onError: () => void): Promise<'connected' | 'disabled'>;
  disconnect(): void;
  loadOlder(): Promise<ChatBatchView>;
  send(username: string, message: string): Promise<void>;
}

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`Chat-Element fehlt: ${selector}`);
  return element;
}

function dayKey(timestamp: number): string {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function formatChatDay(timestamp: number, now = new Date()): string {
  if (dayKey(timestamp) === dayKey(now.getTime())) return 'Heute';
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey(timestamp) === dayKey(yesterday.getTime())) return 'Gestern';
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long',
    ...(new Date(timestamp).getFullYear() === now.getFullYear() ? {} : { year: 'numeric' as const }),
  }).format(new Date(timestamp));
}

export class ChatDialogController {
  private readonly dialog: HTMLDialogElement;
  private readonly status: HTMLElement;
  private readonly messagesElement: HTMLElement;
  private readonly historyControl: HTMLElement;
  private readonly loadMoreButton: HTMLButtonElement;
  private readonly usernameInput: HTMLInputElement;
  private readonly messageInput: HTMLTextAreaElement;
  private readonly characterCount: HTMLOutputElement;
  private readonly sendButton: HTMLButtonElement;
  private messages: readonly ChatMessageView[] = [];
  private connected = false;
  private hasMore = false;
  private loadingOlder = false;
  private sendLockedUntil = 0;
  private connectionToken = 0;
  private receivedFirstBatch = false;
  private historyError = false;

  constructor(
    root: Document,
    private readonly port: ChatUiPort,
    private readonly onOpenChange?: (open: boolean) => void,
  ) {
    this.dialog = requireElement(root, '[data-chat-dialog]');
    this.status = requireElement(root, '[data-chat-status]');
    this.messagesElement = requireElement(root, '[data-chat-messages]');
    this.historyControl = requireElement(root, '[data-chat-history-control]');
    this.loadMoreButton = requireElement(root, '[data-chat-load-more]');
    this.usernameInput = requireElement(root, '[data-chat-username]');
    this.messageInput = requireElement(root, '[data-chat-input]');
    this.characterCount = requireElement(root, '[data-chat-character-count]');
    this.sendButton = requireElement(root, '[data-chat-send]');
  }

  bind(): () => void {
    const openButton = requireElement<HTMLButtonElement>(document, '[data-chat-button]');
    const closeButton = requireElement<HTMLButtonElement>(this.dialog, '[data-chat-close]');
    const form = requireElement<HTMLFormElement>(this.dialog, '[data-chat-form]');
    const open = (): void => { void this.open(); };
    const close = (): void => this.dialog.close();
    const submit = (event: SubmitEvent): void => { event.preventDefault(); void this.send(); };
    const input = (): void => this.syncComposer();
    const older = (): void => { void this.loadOlder(); };
    openButton.addEventListener('click', open);
    closeButton.addEventListener('click', close);
    form.addEventListener('submit', submit);
    this.messageInput.addEventListener('input', input);
    this.loadMoreButton.addEventListener('click', older);
    this.dialog.addEventListener('close', this.handleClose);
    this.syncComposer();
    return () => {
      openButton.removeEventListener('click', open);
      closeButton.removeEventListener('click', close);
      form.removeEventListener('submit', submit);
      this.messageInput.removeEventListener('input', input);
      this.loadMoreButton.removeEventListener('click', older);
      this.dialog.removeEventListener('close', this.handleClose);
      this.port.disconnect();
    };
  }

  private readonly handleClose = (): void => {
    this.connectionToken += 1;
    this.port.disconnect();
    this.connected = false;
    this.setStatus('idle', 'Nachrichten werden erst beim Öffnen geladen.');
    this.onOpenChange?.(false);
    this.syncComposer();
  };

  private async open(): Promise<void> {
    if (!this.dialog.open) this.dialog.showModal();
    this.onOpenChange?.(true);
    this.messages = [];
    this.hasMore = false;
    this.receivedFirstBatch = false;
    this.historyError = false;
    this.connected = false;
    this.renderMessages();
    this.setStatus('loading', 'Anonymer Zugang wird verbunden …');
    const token = ++this.connectionToken;
    try {
      const result = await this.port.connect((batch) => {
        if (token !== this.connectionToken || !this.dialog.open) return;
        this.connected = true;
        const scrollToBottom = !this.receivedFirstBatch || this.isNearBottom();
        this.merge(batch, scrollToBottom);
        this.receivedFirstBatch = true;
        this.setStatus('connected', 'Live verbunden');
        this.syncComposer();
      }, () => {
        if (token !== this.connectionToken) return;
        this.connected = false;
        this.setStatus('error', 'Chat konnte nicht geladen werden.');
      });
      if (token !== this.connectionToken) return;
      if (result === 'disabled') this.setStatus('disabled', 'Chat ist offline verfügbar, aber Firebase ist nicht verbunden.');
    } catch {
      if (token === this.connectionToken) this.setStatus('error', 'Chatverbindung fehlgeschlagen.');
    }
    this.syncComposer();
  }

  private setStatus(state: string, copy: string): void {
    this.status.dataset.state = state;
    this.status.textContent = copy;
  }

  private merge(batch: ChatBatchView, scrollToBottom: boolean): void {
    const byId = new Map(this.messages.map((message) => [message.id, message]));
    batch.messages.forEach((message) => byId.set(message.id, message));
    this.messages = [...byId.values()].sort((left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id));
    this.hasMore = batch.hasMore;
    this.renderMessages();
    if (scrollToBottom) this.messagesElement.scrollTop = this.messagesElement.scrollHeight;
  }

  private renderMessages(): void {
    this.messagesElement.replaceChildren();
    this.messagesElement.append(this.historyControl);
    if (this.messages.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'chat-empty';
      const title = document.createElement('strong');
      title.textContent = 'Noch keine Nachrichten';
      const copy = document.createElement('span');
      copy.textContent = 'Die globale Lobby erscheint nach dem Verbinden.';
      empty.append(title, copy);
      this.messagesElement.append(empty);
    } else {
      let lastDay = '';
      this.messages.forEach((message) => {
        const currentDay = dayKey(message.createdAt);
        if (currentDay !== lastDay) {
          const separator = document.createElement('div');
          separator.className = 'chat-day-separator';
          const label = document.createElement('span');
          label.textContent = formatChatDay(message.createdAt);
          separator.append(label);
          this.messagesElement.append(separator);
          lastDay = currentDay;
        }
        const article = document.createElement('article');
        article.className = 'chat-message';
        if (message.uid === this.port.currentUserId) article.classList.add('chat-message--own');
        const meta = document.createElement('div');
        meta.className = 'chat-message__meta';
        const name = document.createElement('strong');
        name.textContent = sanitizePlayerName(message.name);
        const time = document.createElement('time');
        time.dateTime = new Date(message.createdAt).toISOString();
        time.textContent = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(message.createdAt);
        const body = document.createElement('p');
        body.textContent = message.text.slice(0, CHAT_MESSAGE_MAX_LENGTH);
        meta.append(name, time);
        article.append(meta, body);
        this.messagesElement.append(article);
      });
    }
    this.syncHistoryControl();
  }

  private syncHistoryControl(): void {
    this.historyControl.hidden = !this.hasMore && !this.loadingOlder;
    this.loadMoreButton.disabled = !this.connected || this.loadingOlder;
    this.loadMoreButton.dataset.state = this.historyError ? 'error' : 'ready';
    this.loadMoreButton.textContent = this.loadingOlder
      ? 'Ältere Nachrichten werden geladen …'
      : this.historyError ? '↻ Ältere erneut laden' : '↑ Ältere Nachrichten laden';
  }

  private isNearBottom(): boolean {
    return this.messagesElement.scrollHeight - this.messagesElement.scrollTop - this.messagesElement.clientHeight < 48;
  }

  private syncComposer(): void {
    this.characterCount.textContent = `${this.messageInput.value.length} / ${CHAT_MESSAGE_MAX_LENGTH}`;
    this.messageInput.disabled = !this.connected;
    this.sendButton.disabled = !this.connected
      || this.messageInput.value.trim().length === 0
      || Date.now() < this.sendLockedUntil;
  }

  private async loadOlder(): Promise<void> {
    if (!this.connected || !this.hasMore || this.loadingOlder) return;
    this.loadingOlder = true;
    this.historyError = false;
    this.syncHistoryControl();
    const previousHeight = this.messagesElement.scrollHeight;
    const previousTop = this.messagesElement.scrollTop;
    try {
      const batch = await this.port.loadOlder();
      this.merge(batch, false);
      this.messagesElement.scrollTop = previousTop + this.messagesElement.scrollHeight - previousHeight;
    } catch {
      this.historyError = true;
    } finally {
      this.loadingOlder = false;
      this.syncHistoryControl();
    }
  }

  private async send(): Promise<void> {
    if (!this.connected || Date.now() < this.sendLockedUntil) return;
    const username = sanitizePlayerName(this.usernameInput.value);
    const message = this.messageInput.value.replace(/\r/g, '').trim().slice(0, CHAT_MESSAGE_MAX_LENGTH);
    if (!message) return;
    this.usernameInput.value = username;
    this.sendLockedUntil = Date.now() + CHAT_SEND_COOLDOWN_MS;
    this.syncComposer();
    try {
      await this.port.send(username, message);
      this.messageInput.value = '';
    } catch {
      this.setStatus('error', 'Nachricht konnte nicht gesendet werden.');
    } finally {
      globalThis.setTimeout(() => this.syncComposer(), Math.max(0, this.sendLockedUntil - Date.now()));
      this.syncComposer();
    }
  }
}
